import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Avatar,
  CardMedia,
  Link,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  FitnessCenter as FitnessCenterIcon,
  PlayArrow as PlayArrowIcon,
  Build as EquipmentIcon,
  Info as InfoIcon,
  Assignment as InstructionsIcon,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Detailed view for a specific exercise showing all information
 */
const ExerciseDetails = ({ exerciseId, exercise, onBack }) => {
  const getYouTubeId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const formatEquipmentName = (equipment) => {
    return equipment
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2 }}
          color="primary"
        >
          Back to Workout
        </Button>

        {/* Exercise Header */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <Box
            sx={{
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 56,
                height: 56,
              }}
            >
              <FitnessCenterIcon fontSize="large" />
            </Avatar>

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                {exercise.name}
              </Typography>
              <Typography variant="h6" component="h2" sx={{ opacity: 0.9 }}>
                Exercise Details
              </Typography>
            </Box>
          </Box>
        </Card>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Description */}
          {exercise.description && (
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <InfoIcon color="primary" />
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    Description
                  </Typography>
                </Stack>
                <Box sx={{ "& p": { mb: 2 }, "& ul, & ol": { pl: 3, mb: 2 } }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {exercise.description}
                  </ReactMarkdown>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          {exercise.instructions && (
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <InstructionsIcon color="primary" />
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    Instructions
                  </Typography>
                </Stack>
                <Box sx={{ "& p": { mb: 2 }, "& ul, & ol": { pl: 3, mb: 2 } }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {exercise.instructions}
                  </ReactMarkdown>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Videos */}
          {exercise.videos && exercise.videos.length > 0 && (
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 3 }}
                >
                  <PlayArrowIcon color="primary" />
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    How-to Videos
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  {exercise.videos.map((video, videoIndex) => {
                    const videoId = getYouTubeId(video);

                    return (
                      <Grid item xs={12} sm={6} key={videoIndex}>
                        <Card variant="outlined" sx={{ position: "relative" }}>
                          {videoId ? (
                            <>
                              <CardMedia
                                component="img"
                                height="180"
                                image={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                alt={`${exercise.name} demonstration`}
                                onError={(e) => {
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 56, // Account for CardContent height
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "rgba(0,0,0,0.3)",
                                  cursor: "pointer",
                                  borderRadius: "4px 4px 0 0",
                                }}
                                onClick={() => window.open(video, "_blank")}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "rgba(255,255,255,0.9)",
                                    color: "primary.main",
                                    width: 64,
                                    height: 64,
                                  }}
                                >
                                  <PlayArrowIcon fontSize="large" />
                                </Avatar>
                              </Box>
                              <CardContent sx={{ textAlign: "center" }}>
                                <Link
                                  href={video}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  underline="none"
                                >
                                  <Button variant="contained" fullWidth>
                                    Watch on YouTube
                                  </Button>
                                </Link>
                              </CardContent>
                            </>
                          ) : (
                            <CardContent sx={{ textAlign: "center" }}>
                              <PlayArrowIcon
                                color="primary"
                                sx={{ fontSize: 48, mb: 2 }}
                              />
                              <Link
                                href={video}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                              >
                                <Button variant="outlined" fullWidth>
                                  View Video Tutorial
                                </Button>
                              </Link>
                            </CardContent>
                          )}
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Images */}
          {exercise.images && exercise.images.length > 0 && (
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  Exercise Images
                </Typography>

                <Grid container spacing={2}>
                  {exercise.images.map((image, imageIndex) => (
                    <Grid item xs={12} sm={6} md={4} key={imageIndex}>
                      <Card variant="outlined">
                        <CardMedia
                          component="img"
                          height="200"
                          image={image}
                          alt={`${exercise.name} demonstration ${imageIndex + 1}`}
                          sx={{ objectFit: "cover" }}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Equipment */}
          {exercise.equipment && exercise.equipment.length > 0 && (
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <EquipmentIcon color="primary" />
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    Equipment Needed
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  {exercise.equipment.map((equip, equipIndex) => (
                    <Chip
                      key={equipIndex}
                      label={formatEquipmentName(equip)}
                      color="primary"
                      variant="outlined"
                      sx={{ justifyContent: "flex-start" }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Quick Tips */}
          <Card elevation={1} sx={{ bgcolor: "grey.50" }}>
            <CardContent>
              <Typography
                variant="h6"
                component="h3"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Quick Tips
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  • Focus on proper form over heavy weight
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Control the movement throughout the full range of motion
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Breathe consistently during the exercise
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Stop if you feel any pain or discomfort
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExerciseDetails;
